import {
  CampaignMessage,
  getFormattedAmount,
  InvoiceListItem,
  InvoiceMessage,
  MessageType,
  OrderMessage,
  PaymentMessage,
  TemplateKeys,
  TemplateParams,
} from '@zonofi/common';
import { isArray } from 'lodash';
import moment from 'moment';

export const getOrderTemplateParams = (
  data: OrderMessage,
  sellerName: string
): Partial<Record<TemplateKeys, TemplateParams>> => {
  return {
    new_order_created: [
      {
        type: 'header',
        parameters: [
          {
            type: 'image',
            image: {
              link: 'https://zono-digital-qa-templates.s3.ap-south-1.amazonaws.com/whatsappImages/Draft+Order.png',
            },
          },
        ],
      },
      {
        type: 'body',
        parameters: [
          {
            type: 'text',
            text:
              data?.orderLines
                ?.map((line) => `${line?.skuName} - ${line?.quantity}`)
                .join('\n') || '',
          },
          { type: 'text', text: '' },
          { type: 'text', text: data?.itemCount?.toString() || '-' },
          { type: 'text', text: '' },
          { type: 'text', text: sellerName },
        ],
      },
    ],
    order_submitted: [
      {
        type: 'header',
        parameters: [
          {
            type: 'image',
            image: {
              link: 'https://zono-digital-qa-templates.s3.ap-south-1.amazonaws.com/whatsappImages/Order+Received.png',
            },
          },
        ],
      },
      {
        type: 'body',
        parameters: [
          { type: 'text', text: data?.refOrderNumber || '-' },
          { type: 'text', text: data?.itemCount?.toString() || '-' },
          {
            type: 'text',
            text: `${getFormattedAmount(Number(data?.totalAmount) || 0)} ${
              Number(data?.orderSaving)
                ? `| Discount: ${getFormattedAmount(
                    Number(data?.orderSaving) || 0
                  )}`
                : ''
            } ${
              Number(data?.tax)
                ? ` | Tax: ${getFormattedAmount(Number(data?.tax) || 0)}`
                : ''
            }`,
          },
          {
            type: 'text',
            text: getFormattedAmount(Number(data?.netAmount) || 0),
          },
          { type: 'text', text: sellerName },
        ],
      },
    ],
    order_modified: [
      {
        type: 'header',
        parameters: [
          {
            type: 'image',
            image: {
              link: 'https://zono-digital-qa-templates.s3.ap-south-1.amazonaws.com/whatsappImages/Order+modified.png',
            },
          },
        ],
      },
      {
        type: 'body',
        parameters: [
          { type: 'text', text: data?.refOrderNumber || '-' },
          { type: 'text', text: data?.itemCount?.toString() || '-' },
          {
            type: 'text',
            text: `${getFormattedAmount(Number(data?.totalAmount) || 0)} ${
              Number(data?.orderSaving)
                ? `| Discount: ${getFormattedAmount(
                    Number(data?.orderSaving) || 0
                  )}`
                : ''
            } ${
              Number(data?.tax)
                ? ` | Tax: ${getFormattedAmount(Number(data?.tax) || 0)}`
                : ''
            }`,
          },
          {
            type: 'text',
            text: getFormattedAmount(Number(data?.netAmount) || 0),
          },
          { type: 'text', text: sellerName },
        ],
      },
    ],
    order_confirmed_seller: [
      {
        type: 'header',
        parameters: [
          {
            type: 'image',
            image: {
              link: 'https://zono-digital-qa-templates.s3.ap-south-1.amazonaws.com/whatsappImages/ORDER+Confirmed.png',
            },
          },
        ],
      },
      {
        type: 'body',
        parameters: [
          { type: 'text', text: data?.refOrderNumber || '-' },
          { type: 'text', text: data?.itemCount?.toString() || '-' },
          {
            type: 'text',
            text: `${getFormattedAmount(Number(data?.totalAmount) || 0)} ${
              Number(data?.orderSaving)
                ? `| Discount: ${getFormattedAmount(
                    Number(data?.orderSaving) || 0
                  )}`
                : ''
            } ${
              Number(data?.tax)
                ? ` | Tax: ${getFormattedAmount(Number(data?.tax) || 0)}`
                : ''
            }`,
          },
          {
            type: 'text',
            text: getFormattedAmount(Number(data?.netAmount) || 0),
          },
          { type: 'text', text: sellerName },
        ],
      },
    ],
  };
};

export const getInvoiceTemplateParams = (
  data: InvoiceMessage,
  sellerName: string
): Partial<Record<TemplateKeys, TemplateParams>> => {
  return {
    invoice: [
      {
        type: 'header',
        parameters: [
          {
            type: 'image',
            image: {
              link: 'https://zono-digital-qa-templates.s3.ap-south-1.amazonaws.com/whatsappImages/Group+377488.png',
            },
          },
        ],
      },
      {
        type: 'body',
        parameters: [
          { type: 'text', text: data?.refOrderNumber || '-' },
          { type: 'text', text: data?.invoiceNumber || '-' },
          {
            type: 'text',
            text: getFormattedAmount(Number(data?.invoiceAmount) || 0, true),
          },
          {
            type: 'text',
            text: data?.invoiceDate
              ? moment(data?.invoiceDate).format('DD/MM/YYYY')
              : '-',
          },
          {
            type: 'text',
            text: data?.dueDate
              ? moment(data?.dueDate).format('DD/MM/YYYY')
              : '-',
          },
          { type: 'text', text: sellerName },
        ],
      },
    ],
  };
};

export const getPaymentTemplateParams = (
  data: PaymentMessage,
  sellerName: string,
  customerName: string,
  fileUrl: string = ''
): Partial<Record<TemplateKeys, TemplateParams>> => {
  const invoice = isArray(data?.invoice) ? data?.invoice : [];
  return {
    payment_request_with_invoices: [
      {
        type: 'body',
        parameters: [
          {
            text: customerName,
            type: 'text',
          },
          {
            text: getFormattedAmount(data?.amount),
            type: 'text',
          },
          {
            text:
              `${invoice
                ?.map(({ docNumber }) => docNumber)
                .slice(0, 2)
                .join(', ')}${(invoice || [])?.length > 2 ? ', ...' : ''}` ||
              '-',

            type: 'text',
          },
          {
            text: invoice[0]?.invoiceDate || '-',
            type: 'text',
          },
          {
            text: sellerName,
            type: 'text',
          },
          {
            text: '   ',
            type: 'text',
          },
        ],
      },
      {
        type: 'header',
        parameters: [
          {
            image: {
              link: fileUrl,
            },
            type: 'image',
          },
        ],
      },
    ],
    payment_confirmation: [
      {
        type: 'body',
        parameters: [
          {
            text: moment(data.paymentDate)
              .add(-5, 'hours')
              .add(-30, 'minutes')
              .format('DD/MM/YYYY, h:mm a'),
            type: 'text',
          },
          {
            text: getFormattedAmount(data?.amountReceived || 0),
            type: 'text',
          },
          {
            text: data?.bankTransactionId || '-',
            type: 'text',
          },
          {
            text: data?.upiId || '-',
            type: 'text',
          },
          {
            text: data?.paymentRefCode || '-',
            type: 'text',
          },
          {
            text: sellerName,
            type: 'text',
          },
        ],
      },
      {
        type: 'header',
        parameters: [
          {
            image: {
              link: 'https://zono-digital-qa-templates.s3.ap-south-1.amazonaws.com/whatsappImages/MicrosoftTeams-image(1).png',
            },
            type: 'image',
          },
        ],
      },
    ],
    payment_request_with_amount: [
      {
        type: 'body',
        parameters: [
          {
            text: customerName,
            type: 'text',
          },
          {
            text: getFormattedAmount(data?.amount),
            type: 'text',
          },
          {
            text: sellerName,
            type: 'text',
          },
        ],
      },
      {
        type: 'header',
        parameters: [
          {
            image: {
              link: fileUrl,
            },
            type: 'image',
          },
        ],
      },
    ],
  };
};

export const getListTemplateParams = (
  data: any
): Partial<Record<TemplateKeys, TemplateParams>> => {
  if (!isArray(data)) return { invoice_list: [] };
  return {
    invoice_list: [
      {
        type: 'header',
        parameters: [{ type: 'text', text: data?.length?.toString() }],
      },
      {
        type: 'body',
        parameters: [
          {
            type: 'text',
            text: data
              ?.map(
                (item: InvoiceListItem, index) =>
                  `${index > 8 ? index + 1 : ` ${index + 1}`}. ${
                    item.docNumber
                  } | Due Date: ${moment(item.dueDate).format(
                    'Do MMM'
                  )} | Amount: ${getFormattedAmount(
                    Number(item.invoiceAmount)
                  )}`
              )
              .join('\n'),
          },
        ],
      },
    ],
  };
};

export const getCampaignParams = (
  data: CampaignMessage
): Partial<Record<TemplateKeys, TemplateParams>> => {
  return {
    campaign: [
      {
        type: 'body',
        parameters: [
          {
            text: data.sellerName,
            type: 'text',
          },
          {
            text: `*${data.name}*`,
            type: 'text',
          },
          {
            text: data.description,
            type: 'text',
          },
        ],
      },
      {
        type: 'header',
        parameters: [
          {
            image: {
              link: data.assetUrl,
            },
            type: 'image',
          },
        ],
      },
    ],
  };
};

export const getTemplateKey = (
  status: string,
  templateName?: string
): TemplateKeys | undefined => {
  switch (status.toLowerCase()) {
    case 'addingitems':
      return 'new_order_created';
    case 'submittedbycustomer':
      return 'order_submitted';
    case 'modified':
      return 'order_modified';
    case 'approved':
      return 'order_confirmed_seller';
    case 'invoice':
      return 'invoice';
    case 'requestwithinvoice':
      return 'payment_request_with_invoices';
    case 'requestwithoutinvoice':
      return 'payment_request_with_amount';
    case 'snt':
      return 'payment_received';
    case MessageType.INVOICE_LIST.toLowerCase():
      return 'invoice_list';
    case MessageType.CAMPAIGN.toLowerCase():
      return 'campaign';
    case MessageType.DISPATCH.toLowerCase():
      return 'dispatch';
    case MessageType.FORWARD.toLowerCase():
    case MessageType.ISSUE.toLowerCase():
      return 'media';
    case MessageType.SURVEY_FORM.toLowerCase():
      return 'survey-form';
    case MessageType.CONTACT:
    case MessageType.CONTACTS:
      return 'contact';
    default:
      if (templateName?.includes('campaign')) return 'campaign';
      if (templateName?.includes('order')) return 'new_order_created';
      return undefined;
  }
};
